
import { PropertyCard } from "@/components/PropertyCard";
import PropertyListItem from "@/components/property/PropertyListItem";
import { LoadingSkeleton } from "@/components/ui/loading-skeleton";
import { Listing } from "@/lib/types";
import { memo } from "react";

interface PropertyGridProps {
  properties: Listing[];
  isLoading: boolean;
  displayMode: "grid" | "list";
  onClearFilters: () => void;
  isFeatured?: boolean;
  getBoostedStatus?: (id: number | string) => boolean; // Updated to accept string or number
  isCompact?: boolean;
}

// Memoize the empty state component
const EmptyState = memo(
  ({ onClearFilters }: { onClearFilters: () => void }) => (
    <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-gray-100">
      <h3 className="text-xl font-semibold mb-2 text-propertylk-darkblue">
        No properties found
      </h3>
      <p className="text-gray-600 mb-4">
        Try adjusting your filters or search criteria to see more results.
      </p>
      <button
        onClick={onClearFilters}
        className="bg-propertylk-blue hover:bg-propertylk-darkblue transition-colors text-white px-4 py-2 rounded"
      >
        Clear All Filters
      </button>
    </div>
  )
);

EmptyState.displayName = "EmptyState";

// Memoize the entire PropertyGrid component
const PropertyGrid = memo(
  ({
    properties,
    isLoading,
    displayMode,
    onClearFilters,
    isFeatured = false,
    getBoostedStatus,
    isCompact = false,
  }: PropertyGridProps) => {
    if (isLoading) {
      return (
        <LoadingSkeleton
          count={6}
          displayMode={displayMode}
          isCompact={isCompact}
        />
      );
    }

    if (properties.length === 0) {
      return <EmptyState onClearFilters={onClearFilters} />;
    }

    return (
      <div
        className={`grid gap-6 ${
          displayMode === "grid"
            ? isCompact
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            : "grid-cols-1"
        }`}
      >
        {properties.map((property) =>
          displayMode === "grid" ? (
            <PropertyCard 
              key={property.id} 
              property={{
                ...property,
                // Ensure featured flag is properly set
                featured: property.is_featured || property.featured,
                // Add boosted status if available via the callback
                boosted: getBoostedStatus ? getBoostedStatus(property.id || 0) : (property.is_boosted || property.boosted)
              }} 
            />
          ) : (
            <PropertyListItem 
              key={property.id} 
              property={{
                ...property,
                featured: property.is_featured || property.featured,
                boosted: getBoostedStatus ? getBoostedStatus(property.id || 0) : (property.is_boosted || property.boosted)
              }} 
            />
          )
        )}
      </div>
    );
  }
);

PropertyGrid.displayName = "PropertyGrid";

export default PropertyGrid;
